import React from 'react';
import { User } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BracketButton from '../components/BracketButton';

const founders = [
  {
    name: 'Landon Gonce',
    role: 'Co-Founder',
    focus: 'Operations & Transportation',
    bio: "Landon has dedicated his career to transportation and operations leadership — managing fleets, leading teams, and developing solutions for some of the nation's largest energy and industrial companies. His hands-on experience in the field is what keeps Caprock's dispatch sharp and its drivers accountable.",
    initials: 'LG',
    photo: '/media/team/landon.png',
  },
  {
    name: 'Quenton Persinger',
    role: 'Co-Founder',
    focus: 'Business Leadership & Strategy',
    bio: "Quenton brings decades of business leadership, strategic vision, and an unwavering commitment to building organizations that create lasting value. He guides Caprock's growth with the same principles that built the company: integrity, hard work, and keeping your word.",
    initials: 'QP',
    photo: '/media/team/quenton.png',
  },
];

const AboutPage = () => {
  return (
    <div className="bg-white">
      <Navbar />

      {/* Hero */}
      <div className="pt-32 pb-16 max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-8 h-[1.5px] bg-black/60" />
          <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-black/60">About Caprock</span>
        </div>
        <h1 className="big-display text-[64px] sm:text-[96px] lg:text-[140px] leading-[0.9]">
          Built On Integrity.
          <br />
          <span className="text-black/30">Kept On The Road.</span>
        </h1>
        <p className="font-body text-black/70 max-w-2xl mt-8 leading-relaxed text-[17px]">
          Caprock Logistics Group was founded on a simple belief: business should be built on integrity, hard work, and keeping your word.
        </p>
      </div>

      {/* Our Story */}
      <section className="bg-white py-24 lg:py-32 border-t border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-[1.5px] bg-black/60" />
                <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-black/60">01 · Our Story</span>
              </div>
              <h2 className="big-display text-[48px] lg:text-[72px] leading-[0.95]">
                Why We
                <br />
                <span className="text-black/40">Started.</span>
              </h2>
            </div>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-[16px] leading-relaxed text-black/80">
            <p>
              After spending more than a decade in the transportation and oilfield industries, founders <strong className="text-black">Landon Gonce</strong> and <strong className="text-black">Quenton Persinger</strong> saw an opportunity to build a logistics company that puts relationships, safety, and exceptional service ahead of everything else.
            </p>
            <p>
              Together, they founded Caprock Logistics Group to provide dependable transportation for equipment, industrial freight, oilfield materials, and specialized loads throughout Texas and beyond.
            </p>
            <p>
              At Caprock, we understand that every shipment matters. Whether we're moving rental equipment across town or delivering critical materials to a job site, we approach every load with professionalism, attention to detail, and a commitment to doing the job right the first time.
            </p>
            <div className="pt-6 border-t border-black/10">
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-black/50 mb-3">Our Mission</div>
              <p className="font-display text-[24px] lg:text-[28px] leading-[1.25] text-black">
                Deliver every load safely, on time, and with the level of service that earns long-term partnerships.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Faith-driven block */}
      <section className="bg-neutral-950 text-white py-24 lg:py-28">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-8">
            <span className="w-8 h-[1.5px] bg-white/50" />
            <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-white/70">Our Foundation</span>
            <span className="w-8 h-[1.5px] bg-white/50" />
          </div>
          <p className="font-display text-[28px] sm:text-[36px] lg:text-[44px] leading-[1.2]">
            As a faith-driven company, we believe integrity isn't just part of our business — <span className="text-white/50">it's the foundation of it.</span>
          </p>
          <p className="font-body text-white/70 text-[15px] lg:text-base leading-relaxed max-w-2xl mx-auto mt-8">
            We strive to honor God in the way we serve our customers, care for our employees, and conduct ourselves every day. We're not just hauling freight — we're building relationships that last.
          </p>
        </div>
      </section>

      {/* Leadership */}
      <section id="leadership" className="bg-white py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-6">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-[1.5px] bg-black/60" />
                <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-black/60">02 · Leadership</span>
              </div>
              <h2 className="big-display text-[48px] lg:text-[80px] leading-[0.95]">
                The People
                <br />
                <span className="text-black/30">Behind Caprock.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="font-body text-black/70 leading-relaxed text-[15px]">
                Customers work with people, not logos. Meet the founders who answer the phone, ride the trucks, and stand behind every load Caprock hauls.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14">
            {founders.map((f) => (
              <div key={f.name} className="group">
                {/* Portrait */}
                <div className="relative aspect-[4/5] overflow-hidden bg-neutral-100 border border-black/10 mb-6">
                  {f.photo ? (
                    <img
                      src={f.photo}
                      alt={f.name}
                      className="absolute inset-0 w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-700"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="relative w-40 h-40 flex items-center justify-center">
                        <span className="big-display text-[110px] leading-none text-black/15 select-none">
                          {f.initials}
                        </span>
                        <User className="absolute w-20 h-20 text-black/30" strokeWidth={1} />
                      </div>
                    </div>
                  )}
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between z-10">
                    <span className={`font-mono-tech text-[9px] tracking-[0.3em] uppercase ${f.photo ? 'text-white/80' : 'text-black/50'}`}>
                      {f.photo ? f.name.split(' ')[0] : 'Photo — Coming Soon'}
                    </span>
                    <span className={`font-mono-tech text-[9px] tracking-[0.3em] uppercase ${f.photo ? 'text-white/80' : 'text-black/50'}`}>
                      TX
                    </span>
                  </div>
                  {/* corner frames */}
                  <span className={`absolute top-3 left-3 w-3 h-3 border-t border-l z-10 ${f.photo ? 'border-white/70' : 'border-black/30'}`} />
                  <span className={`absolute top-3 right-3 w-3 h-3 border-t border-r z-10 ${f.photo ? 'border-white/70' : 'border-black/30'}`} />
                  <span className={`absolute bottom-3 left-3 w-3 h-3 border-b border-l z-10 ${f.photo ? 'border-white/70' : 'border-black/30'}`} />
                  <span className={`absolute bottom-3 right-3 w-3 h-3 border-b border-r z-10 ${f.photo ? 'border-white/70' : 'border-black/30'}`} />
                </div>

                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-black/50 mb-3">
                    {f.role} · {f.focus}
                  </div>
                  <h3 className="big-display text-[38px] lg:text-[46px] leading-none mb-4">
                    {f.name}
                  </h3>
                  <p className="font-body text-black/75 text-[15px] leading-relaxed">
                    {f.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 border-t border-black/10 pt-8 flex flex-wrap items-center justify-between gap-6">
            <p className="font-body text-black/60 text-sm max-w-md">
              Have a load that needs moving? The best way to know if Caprock is the right fit is a direct conversation.
            </p>
            <BracketButton to="/contact" variant="dark">Work With Us</BracketButton>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
